import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import FloatingInput from '../components/ui/FloatingInput';

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to log in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-row">
      {/* ---------------------------------------------------------------- */}
      {/* LEFT PANEL -- branded gradient (desktop only)                    */}
      {/* ---------------------------------------------------------------- */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 flex-col justify-between overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-primary-500/20" />
        <div className="absolute top-1/3 right-0 w-72 h-72 rounded-full bg-accent-500/10 translate-x-1/2" />
        <div className="absolute bottom-0 left-1/4 w-64 h-64 rounded-full bg-primary-500/20 translate-y-1/3" />

        {/* Logo */}
        <div className="relative z-10 p-10">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-soft-sm">
              <span className="text-white font-bold text-xl leading-none">C</span>
            </div>
            <span className="text-h2 text-white">
              Cli<span className="text-primary-200">QQ</span>
            </span>
          </Link>
        </div>

        {/* Hero copy */}
        <div className="relative z-10 px-10 flex-1 flex flex-col justify-center max-w-lg">
          <h2 className="text-display text-white mb-4 leading-tight">
            Discover events.<br />Meet your people.
          </h2>
          <p className="text-body-lg text-primary-200">
            Join a community of explorers, creators, and connectors. Find events
            that spark your curiosity and friendships that last.
          </p>
        </div>

        {/* Copyright */}
        <div className="relative z-10 p-10">
          <p className="text-body-sm text-primary-300/70">
            &copy; {new Date().getFullYear()} CliQQ. All rights reserved.
          </p>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* RIGHT PANEL -- login form                                        */}
      {/* ---------------------------------------------------------------- */}
      <div className="flex-1 flex items-center justify-center bg-surface-50 px-4 py-10 sm:px-8">
        <div className="w-full max-w-md">
          {/* Mobile logo (visible below lg) */}
          <div className="lg:hidden flex justify-center mb-8">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-soft-sm">
                <span className="text-white font-bold text-xl leading-none">C</span>
              </div>
              <span className="text-h2 text-surface-900">
                Cli<span className="text-primary-500">QQ</span>
              </span>
            </Link>
          </div>

          {/* Heading */}
          <h1 className="text-h1 text-surface-900 mb-1">Welcome back</h1>
          <p className="text-body text-surface-500 mb-8">
            Enter your credentials to access your account
          </p>

          {/* Error display */}
          {error && (
            <div
              className="flex items-start gap-3 rounded-xl bg-error-50 border border-error-400/30 p-4 mb-6"
              role="alert"
            >
              <svg
                className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-body-sm text-error-600">{error}</span>
            </div>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <FloatingInput
              label="Email address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
            />

            <FloatingInput
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
            />

            {/* Remember me + Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-surface-300 text-primary-500 focus:ring-primary-500/30 transition"
                />
                <span className="text-body-sm text-surface-600">Remember me</span>
              </label>

              <Link
                to="/forgot-password"
                className="text-body-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 text-white text-body font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 shadow-colored"
            >
              {loading && (
                <svg
                  className="w-5 h-5 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              )}
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-surface-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-surface-50 px-3 text-body-sm text-surface-400">
                Or continue with
              </span>
            </div>
          </div>

          {/* Social login buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-surface-200 rounded-xl text-body-sm font-semibold text-surface-700 hover:bg-surface-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-1 transition-colors duration-200"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google
            </button>

            <button
              type="button"
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-surface-200 rounded-xl text-body-sm font-semibold text-surface-700 hover:bg-surface-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-1 transition-colors duration-200"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.53-3.23 0-1.44.66-2.2.47-3.06-.4C3.79 16.17 4.36 9.03 8.93 8.77c1.27.06 2.15.72 2.91.76.99-.2 1.94-.78 3-.84 1.26.1 2.22.57 2.85 1.43-2.6 1.53-1.98 4.9.6 5.84-.48 1.22-.87 2.41-1.24 3.32zM12.03 8.7c-.14-2.33 1.83-4.34 4.01-4.54.28 2.6-2.38 4.55-4.01 4.54z" />
              </svg>
              Apple
            </button>
          </div>

          {/* Sign up link */}
          <p className="text-center text-body-sm text-surface-500 mt-8">
            Don't have an account?{' '}
            {/* Start at account-type selection so the choice carries into the
                form. That page was routed but orphaned -- nothing linked to it
                -- so business/organization signup had no entry point. */}
            <Link
              to="/account-type"
              className="font-semibold text-primary-600 hover:text-primary-700 transition-colors"
            >
              Sign up free
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
